import { useNavigate } from 'react-router-dom';
import { useOrders, Order } from '../hooks/useOrders';

// Payment method icons
const PAYMENT_ICONS: Record<string, string> = {
  bank_transfer: '🏦',
  momo: '💜',
  zalopay: '💙',
  viettelpay: '❤️',
  vnpay: '💚',
  wise: '🌍',
  revolut: '💳',
  paypal: '🅿️',
  skrill: '💰',
  payoneer: '💵',
  pingpong: '🏓',
  cash: '💸',
};

// Status colors and labels
const STATUS_INFO: Record<string, { label: string; color: string }> = {
  open: { label: 'Đang mở', color: 'bg-blue-500' },
  matched: { label: 'Đã khớp', color: 'bg-green-500' },
  escrow: { label: 'Escrow', color: 'bg-yellow-500' },
  completed: { label: 'Hoàn thành', color: 'bg-green-600' },
  cancelled: { label: 'Đã hủy', color: 'bg-gray-500' },
  disputed: { label: 'Tranh chấp', color: 'bg-red-500' },
};

function formatNumber(num: number): string {
  return new Intl.NumberFormat('vi-VN').format(num);
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  if (diff < 60000) return 'Vừa xong';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} phút trước`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} giờ trước`;
  return date.toLocaleDateString('vi-VN');
}

function OrderCard({ order, onCancel, onChat }: { 
  order: Order; 
  onCancel: () => void;
  onChat: () => void;
}) {
  const status = STATUS_INFO[order.status] || STATUS_INFO.open;
  
  return (
    <div className="bg-telegram-secondary rounded-xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${status.color}`} />
          <span className="text-sm text-telegram-hint">{status.label}</span>
        </div>
        <span className="text-xs text-telegram-hint">{formatTime(order.createdAt)}</span>
      </div>
      
      {/* Main info */}
      <div className="flex items-center justify-between">
        <div>
          <span className={`text-lg font-bold ${order.type === 'buy' ? 'text-green-500' : 'text-blue-500'}`}>
            {order.type === 'buy' ? 'MUA' : 'BÁN'}
          </span>
          <span className="ml-2 font-semibold">{order.amount} {order.crypto}</span>
          <span className="ml-1 text-telegram-hint text-sm">({order.network})</span>
        </div>
        <div className="text-right">
          <div className="font-semibold">{formatNumber(order.price)} {order.fiat}</div>
          <div className="text-xs text-telegram-hint">per {order.crypto}</div>
        </div>
      </div>
      
      {/* Total */}
      <div className="flex items-center justify-between bg-telegram-bg/50 rounded-lg p-2">
        <span className="text-telegram-hint">Tổng:</span>
        <span className="font-bold">{formatNumber(order.totalFiat)} {order.fiat}</span>
      </div>
      
      {/* Payment methods */}
      <div className="flex flex-wrap gap-1">
        {order.paymentMethods.map(pm => (
          <span key={pm} className="text-sm bg-telegram-bg/50 px-2 py-1 rounded">
            {PAYMENT_ICONS[pm] || '💳'}
          </span>
        ))}
      </div>
      
      {/* Actions */}
      <div className="flex gap-2">
        {order.status === 'open' && (
          <button
            onClick={onCancel}
            className="flex-1 py-2 rounded-lg bg-red-500/10 text-red-500 text-sm font-medium"
          >
            Hủy lệnh
          </button>
        )}
        {(order.status === 'matched' || order.status === 'escrow') && (
          <button
            onClick={onChat}
            className="flex-1 py-2 rounded-lg bg-telegram-button text-white text-sm font-medium"
          >
            💬 Chat
          </button>
        )}
      </div>
    </div>
  );
}

export default function Orders() {
  const navigate = useNavigate();
  const user = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const { orders, loading, error, refetch, cancelOrder } = useOrders(user?.id);

  const handleCancel = async (orderId: string) => {
    if (window.confirm('Bạn có chắc muốn hủy lệnh này?')) {
      await cancelOrder(orderId);
    }
  };

  const handleChat = (order: Order) => {
    // Navigate to chat with order session
    navigate(`/chat/${order.id}`);
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin text-4xl mb-2">⏳</div>
        <p className="text-telegram-hint">Đang tải...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <div className="text-4xl mb-2">❌</div>
        <p className="text-red-500">{error}</p>
        <button
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-telegram-button text-white rounded-lg"
        >
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">📋 Lệnh của tôi</h1>
        <button
          onClick={refetch}
          className="text-telegram-button text-sm"
        >
          🔄 Làm mới
        </button>
      </div>
      
      {orders.length === 0 ? (
        <div className="text-center text-telegram-hint py-12">
          <div className="text-4xl mb-2">📭</div>
          <p>Chưa có lệnh nào</p>
          <p className="text-sm mt-1">Bấm Mua/Bán để tạo lệnh mới</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 bg-telegram-button text-white rounded-lg"
          >
            Tạo lệnh
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              onCancel={() => handleCancel(order.id)}
              onChat={() => handleChat(order)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
